/**
 * StepperField – react-hook-form compatible numeric stepper.
 * Minus/plus buttons + editable text input.
 * Mobile-friendly: 44px min hit area on buttons.
 */
import { useCallback } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { cn } from '@/lib/utils';
import type { UseFormReturn, FieldValues, Path } from 'react-hook-form';

export interface StepperFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
}

export function StepperField<T extends FieldValues>({
  form,
  name,
  label,
  description,
  min = 0,
  max,
  step = 1,
  disabled = false,
  className,
}: StepperFieldProps<T>) {
  const clamp = useCallback(
    (v: number) => {
      let n = Math.round(v);
      if (min != null) n = Math.max(min, n);
      if (max != null) n = Math.min(max, n);
      return n;
    },
    [min, max],
  );

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const value = typeof field.value === 'number' ? field.value : min ?? 0;
        const atMin = min != null && value <= min;
        const atMax = max != null && value >= max;

        return (
          <FormItem className={className}>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <div className="flex items-center gap-0">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={disabled || atMin}
                  className="h-11 w-11 rounded-r-none border-r-0 shrink-0"
                  onClick={() => field.onChange(clamp(value - step))}
                  aria-label={`Minska ${label}`}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  inputMode="numeric"
                  disabled={disabled}
                  className={cn(
                    "h-11 rounded-none text-center [appearance:textfield]",
                    "[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                    "w-16"
                  )}
                  value={value}
                  min={min}
                  max={max}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') return;
                    field.onChange(clamp(Number(raw)));
                  }}
                  onBlur={() => field.onChange(clamp(value))}
                  aria-label={label}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={disabled || atMax}
                  className="h-11 w-11 rounded-l-none border-l-0 shrink-0"
                  onClick={() => field.onChange(clamp(value + step))}
                  aria-label={`Öka ${label}`}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
